import api from '../utils/api';
import {
  clearCookieSessionHint,
  hasCookieSessionHint,
} from '../auth/authSessionHint';

const OTP_TIMEOUT_MS = 8000;

/**
 * Verify OTP and establish httpOnly cookie session (B-01B).
 */
export async function verifyLoginOtp({ otpId, phone, otp }) {
  const portal = localStorage.getItem('authPortal') || 'new';
  const response = await api.post(
    '/auth/verify-otp',
    { otpId, phone, otp, portal },
    { timeout: OTP_TIMEOUT_MS, withCredentials: true }
  );
  return response.data;
}

/**
 * Resend OTP for existing session.
 */
export async function resendLoginOtp({ otpId, phone }) {
  const response = await api.post(
    '/auth/resend-otp',
    { otpId, phone },
    { timeout: OTP_TIMEOUT_MS, withCredentials: true }
  );
  return response.data;
}

export function mapVerifyOtpError(error) {
  const status = error.response?.status;
  const data = error.response?.data || {};

  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return { message: 'Request timed out. Please try again.', clearOtp: false };
  }

  if (!error.response && (error.message === 'Network Error' || !navigator.onLine)) {
    return { message: 'No internet connection. Check your network.', toast: true, clearOtp: false };
  }

  if (status === 410) {
    return { message: 'OTP expired. Request a new one.', clearOtp: true, expired: true };
  }

  if (status === 423) {
    return {
      message: 'Account temporarily locked. Try again after 24 hours.',
      clearOtp: true,
      locked: true,
      attemptsLeft: 0,
    };
  }

  if (status === 400 && data.attemptsLeft !== undefined) {
    return {
      message: data.message || `Incorrect OTP. ${data.attemptsLeft} attempts remaining.`,
      attemptsLeft: data.attemptsLeft,
      clearOtp: true,
    };
  }

  return {
    message: data.message || error.message || 'OTP verification failed',
    clearOtp: false,
  };
}

/**
 * Clear httpOnly auth cookies (B-01B logout).
 */
export async function logoutCookieSession() {
  clearCookieSessionHint();
  await api.post('/auth/logout', {}, { withCredentials: true });
}

/**
 * Refresh access token using httpOnly refresh cookie.
 * No-op when there is no client-side session hint (avoids 401 noise for anonymous users).
 */
export async function refreshCookieSession() {
  if (!hasCookieSessionHint()) {
    return null;
  }
  const response = await api.post('/auth/refresh', {}, { withCredentials: true, skipAuthRefresh: true });
  return response.data;
}

export function mapResendOtpError(error) {
  const status = error.response?.status;
  const data = error.response?.data || {};

  if (!error.response && (error.message === 'Network Error' || !navigator.onLine)) {
    return { message: 'No internet connection. Check your network.', toast: true };
  }

  if (status === 410) {
    return { message: 'OTP expired. Request a new one.', expired: true };
  }

  if (status === 423) {
    return { message: 'Account temporarily locked. Try again after 24 hours.', locked: true };
  }

  return {
    message: data.message || error.message || 'Could not resend OTP.',
  };
}
